import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';

import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';

const LANGUAGES = ['ro', 'en'] as const;

/**
 * Extracted from AppLayout.tsx (2026-08-03) so the public/pre-login pages
 * (signup, verify-email, setup wizard) can offer it too — those render
 * outside AppLayout entirely, and a new self-serve visitor whose browser
 * reports a non-'ro' locale + has no stored `i18nextLng` yet (the exact
 * signup scenario, see shared/lib/i18n.ts fallbackLng) had no way to
 * switch language until the main app shell finally rendered.
 */
export function LanguageSwitcher(): JSX.Element {
  const { t, i18n } = useTranslation();
  const current = i18n.language?.slice(0, 2) ?? 'ro';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('common.language')}>
          <Languages className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t('common.language')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGES.map((lng) => (
          <DropdownMenuItem
            key={lng}
            onSelect={() => {
              void i18n.changeLanguage(lng);
            }}
            className={cn(current === lng && 'font-semibold text-primary')}
          >
            {t(`languages.${lng}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
